"use client";
import React from "react";

import { BoxSx, IconName, IconNameType, Target } from "../../foundations";
import { Icon } from "../../foundations/Icon/Icon";
import {
  getResponsiveStyle,
  ResponsiveProp
} from "../../utils/getResponsiveStyle";

export type ButtonProps = React.ComponentProps<typeof Target> & {
  variant?: "filled" | "outline" | "subtle" | "critical";
  brand?: "primary" | "secondary";
  size?: ResponsiveProp<"medium" | "large" | "xlarge">;
  icon?: IconNameType;
  iconPosition?: "start" | "end";
  ["aria-label"]?: string;
  children?: React.ReactNode;
};

export const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant = "filled",
    brand = "primary",
    size = "medium",
    icon,
    iconPosition = "start",
    disabled = false,
    "aria-label": ariaLabel,
    children,
    sx,
    ...restProps
  } = props;

  const isIconButton = icon && !children;
  if (isIconButton && !ariaLabel) {
    throw new Error(
      "Accessibility error: Icon-only buttons must have an aria-label."
    );
  }

  const iconButtonBaseStyles: BoxSx = {
    width: getResponsiveStyle(size, (s) =>
      s === "medium" ? "spacing-9" : s === "large" ? "spacing-10" : "spacing-12"
    ),
    p: getResponsiveStyle(size, (s) =>
      s === "medium" ? "spacing-3" : "spacing-4"
    )
  };

  const iconSize = getResponsiveStyle(size, (s) =>
    isIconButton
      ? s === "medium"
        ? "xs"
        : s === "large"
          ? "sm"
          : "md"
      : s === "medium"
        ? "xxs"
        : s === "large"
          ? "xs"
          : "sm"
  );

  const buttonBaseStyles: BoxSx = {
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    flexShrink: 0,
    height: getResponsiveStyle(size, (s) =>
      s === "medium" ? "spacing-9" : s === "large" ? "spacing-10" : "spacing-12"
    ),
    borderRadius: "radius-2",
    px: "spacing-5",
    py: "spacing-4",
    gap: "spacing-2",
    fontSize: getResponsiveStyle(size, (s) =>
      s === "medium"
        ? "font-size-3"
        : s === "large"
          ? "font-size-4"
          : "font-size-5"
    ),
    fontWeight: "font-weight-medium",
    lineHeight: "leading-none",
    letterSpacing: "kerning-none",
    transition: "transition-color-medium"
  };

  const buttonColorsFilled = React.useMemo<BoxSx>(
    () => ({
      backgroundColor: disabled
        ? "button-surface-filled-disabled"
        : brand === "primary"
          ? "button-surface-filled-enabled"
          : "button-surface-filled-secondary-enabled",
      color: disabled
        ? "button-onSurface-filled-disabled"
        : brand === "primary"
          ? "button-onSurface-filled-primary"
          : "button-onSurface-filled-secondary",
      ":hover": disabled
        ? {}
        : {
            backgroundColor:
              brand === "primary"
                ? "button-surface-filled-hover"
                : "button-surface-filled-secondary-hover"
          },
      ":active": disabled
        ? {}
        : {
            backgroundColor:
              brand === "primary"
                ? "button-surface-filled-click"
                : "button-surface-filled-secondary-click"
          }
    }),
    [brand, disabled]
  );

  const buttonColorsOutline = React.useMemo<BoxSx>(
    () => ({
      backgroundColor: disabled
        ? "button-surface-outline-disabled"
        : brand === "primary"
          ? "button-surface-outline-primary"
          : "button-surface-outline-secondary",
      color: disabled
        ? "button-onSurface-outline-disabled"
        : brand === "primary"
          ? "button-onSurface-outline-enabled"
          : "button-onSurface-outline-secondary-enabled",
      border: "1px solid",
      borderColor: disabled
        ? "button-onSurface-outline-disabled"
        : brand === "primary"
          ? "button-onSurface-outline-enabled"
          : "button-onSurface-outline-secondary-enabled",
      ":hover": disabled
        ? {}
        : {
            color:
              brand === "primary"
                ? "button-onSurface-outline-hover"
                : "button-onSurface-outline-secondary-hover",
            borderColor:
              brand === "primary"
                ? "button-onSurface-outline-hover"
                : "button-onSurface-outline-secondary-hover"
          },
      ":active": disabled
        ? {}
        : {
            color:
              brand === "primary"
                ? "button-onSurface-outline-click"
                : "button-onSurface-outline-secondary-click",
            borderColor:
              brand === "primary"
                ? "button-onSurface-outline-click"
                : "button-onSurface-outline-secondary-click"
          }
    }),
    [brand, disabled]
  );

  const buttonColorsSubtle = React.useMemo<BoxSx>(
    () => ({
      color: disabled
        ? "button-onSurface-subtle-disabled"
        : brand === "primary"
          ? "button-onSurface-subtle-enabled"
          : "button-onSurface-subtle-secondary-enabled",

      ":hover": disabled
        ? {}
        : {
            color:
              brand === "primary"
                ? "button-onSurface-subtle-hover"
                : "button-onSurface-subtle-secondary-hover"
          },
      ":active": disabled
        ? {}
        : {
            color:
              brand === "primary"
                ? "button-onSurface-subtle-click"
                : "button-onSurface-subtle-secondary-click"
          }
    }),
    [brand, disabled]
  );

  const buttonColorCritical = React.useMemo<BoxSx>(
    () => ({
      backgroundColor: disabled
        ? "button-surface-critical-disabled"
        : "button-surface-critical-enabled",
      color: disabled
        ? "button-onSurface-critical-disabled"
        : "button-onSurface-critical",
      ":hover": disabled
        ? {}
        : {
            backgroundColor: "button-surface-critical-hover"
          },
      ":active": disabled
        ? {}
        : {
            backgroundColor: "button-surface-critical-click"
          }
    }),
    [disabled]
  );

  const variantStyles = React.useMemo(() => {
    switch (variant) {
      case "filled":
        return buttonColorsFilled;
      case "outline":
        return buttonColorsOutline;
      case "subtle":
        return buttonColorsSubtle;
      case "critical":
        return buttonColorCritical;
    }
  }, [
    variant,
    buttonColorsFilled,
    buttonColorsOutline,
    buttonColorsSubtle,
    buttonColorCritical
  ]);

  const buttonFocusStyles = React.useMemo<BoxSx>(
    () => ({
      ":focus-visible": {
        outline: "2px solid",
        outlineColor: "focus",
        outlineOffset: "2px"
      }
    }),
    []
  );

  const renderIcon = () => {
    return (
      <Icon
        icon={icon as IconName}
        size={iconSize}
        color="inherit"
        sx={{ flexShrink: 0 }}
      />
    );
  };

  return (
    <Target
      sx={{
        ...buttonBaseStyles,
        ...(isIconButton && iconButtonBaseStyles),
        ...variantStyles,
        ...buttonFocusStyles,
        ...sx
      }}
      disabled={disabled}
      aria-label={ariaLabel}
      {...restProps}
    >
      {icon && iconPosition === "start" && renderIcon()}
      {children}
      {icon && iconPosition === "end" && renderIcon()}
    </Target>
  );
};
